import type { EnrichmentCandidate, EnrichmentResponse } from "@needle-drop/shared";
import { create } from "zustand";
import { db } from "../db";
import type { LibrarySettings, Playlist, SortKey, Track, ViewSelection } from "../model";
import { apiRequest, CloudApiError, fetchCloudLibrary, fromCloudTrack, getLibraryToken, saveCloudPlaylist, saveCloudTrack, setLibraryToken, uploadArtwork } from "../services/cloudLibrary";
import { chooseSyncFolder, syncTracksToFolder } from "../services/folderSync";
import { hashFile, parseAudioMetadata } from "../services/id3";
import { visitMusicFiles } from "../services/musicArchive";
import { createStorage } from "../services/storage";

const productionApiUrl = "https://dj.inbx.page";
const configuredApiUrl = import.meta.env.VITE_API_BASE_URL ?? (import.meta.env.PROD ? productionApiUrl : "http://localhost:8787");
export const cloudLibraryChangeKey = "needle-drop-cloud-library-change";

let latestSyncRequest = 0;
let syncCommitQueue: Promise<void> = Promise.resolve();
let changeAnnouncementTimer: number | undefined;

function announceCloudLibraryChange() {
  window.clearTimeout(changeAnnouncementTimer);
  changeAnnouncementTimer = window.setTimeout(() => {
    localStorage.setItem(cloudLibraryChangeKey, `${Date.now()}-${crypto.randomUUID()}`);
  }, 120);
}

const defaultSettings: LibrarySettings = {
  id: "settings",
  storageMode: "r2",
  apiBaseUrl: configuredApiUrl,
  subtleCrt: true,
};

interface ImportStatus { current: number; total: number; fileName: string; action: "READING ZIP" | "UPLOADING" | "ANALYZING + TAGGING" | "SYNCING FOLDER"; }

interface LibraryState {
  tracks: Track[];
  playlists: Playlist[];
  settings: LibrarySettings;
  selectedIds: string[];
  view: ViewSelection;
  query: string;
  genreFilter: string;
  labelFilter: string;
  sortKey: SortKey;
  sortDirection: "asc" | "desc";
  currentTrackId?: string | undefined;
  importStatus?: ImportStatus | undefined;
  notice?: string | undefined;
  initialized: boolean;
  authenticated: boolean;
  cloudSyncing: boolean;
  lastSyncedAt?: string | undefined;
  authError?: string | undefined;
  initialize(): Promise<void>;
  unlockLibrary(token: string): Promise<boolean>;
  signOut(): void;
  syncLibrary(silent?: boolean): Promise<void>;
  importFiles(files: File[]): Promise<void>;
  updateTrack(id: string, patch: Partial<Track>): Promise<void>;
  updateMany(ids: string[], patch: Partial<Track>): Promise<void>;
  removeTracks(ids: string[]): Promise<void>;
  downloadTracks(ids: string[]): Promise<void>;
  linkSyncFolder(): Promise<void>;
  syncFolder(requestPermission?: boolean): Promise<void>;
  recordPlay(id: string): Promise<void>;
  setSelection(ids: string[]): void;
  toggleSelection(id: string, additive?: boolean, range?: string[]): void;
  setView(view: ViewSelection): void;
  setQuery(query: string): void;
  setGenreFilter(value: string): void;
  setLabelFilter(value: string): void;
  setSort(key: SortKey): void;
  setCurrentTrack(id?: string): void;
  createPlaylist(name: string, type: "manual" | "smart", rules?: Playlist["rules"]): Promise<void>;
  deletePlaylist(id: string): Promise<void>;
  addToPlaylist(playlistId: string, trackIds: string[]): Promise<void>;
  removeFromPlaylist(playlistId: string, trackIds: string[]): Promise<void>;
  enrichTracks(ids: string[]): Promise<void>;
  applySuggestions(trackId: string, fields: Array<keyof EnrichmentCandidate>): Promise<void>;
  updateSettings(patch: Partial<LibrarySettings>): Promise<void>;
  clearNotice(): void;
}

function unique(values: string[]) {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))];
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export const useLibraryStore = create<LibraryState>((set, get) => ({
  tracks: [], playlists: [], settings: defaultSettings, selectedIds: [], view: { view: "all" }, query: "",
  genreFilter: "", labelFilter: "", sortKey: "title", sortDirection: "asc", initialized: false,
  authenticated: false, cloudSyncing: false,

  async initialize() {
    const [cachedTracks, cachedPlaylists, storedSettings] = await Promise.all([db.tracks.toArray(), db.playlists.toArray(), db.settings.get("settings")]);
    const settings: LibrarySettings = storedSettings
      ? { ...storedSettings, storageMode: "r2", apiBaseUrl: import.meta.env.PROD ? configuredApiUrl : storedSettings.apiBaseUrl }
      : defaultSettings;
    await db.settings.put(settings);
    const hasToken = Boolean(getLibraryToken());
    set({ settings, tracks: hasToken ? cachedTracks : [], playlists: hasToken ? cachedPlaylists : [], authenticated: hasToken, initialized: true });
    if (hasToken) {
      try { await get().syncLibrary(true); }
      catch (error) {
        if (error instanceof CloudApiError && error.status === 401) get().signOut();
        else set({ notice: "Cloud is temporarily unavailable. Showing the last synced library." });
      }
    }
  },

  async unlockLibrary(token) {
    setLibraryToken(token);
    set({ authError: undefined });
    try {
      await get().syncLibrary(true);
      set({ authenticated: true });
      return true;
    } catch (error) {
      setLibraryToken("");
      set({ authenticated: false, authError: error instanceof CloudApiError && error.status === 401 ? "That access code is not valid." : "The private cloud library could not be reached." });
      return false;
    }
  },

  signOut() {
    setLibraryToken("");
    set({ authenticated: false, tracks: [], playlists: [], selectedIds: [], currentTrackId: undefined, authError: undefined });
  },

  async syncLibrary(silent = false) {
    if (!getLibraryToken()) throw new CloudApiError(401, "Library access code is missing.");
    if (silent && get().cloudSyncing) return;
    const requestId = ++latestSyncRequest;
    set({ cloudSyncing: true });
    try {
      const cloud = await fetchCloudLibrary(get().settings.apiBaseUrl);
      if (requestId !== latestSyncRequest) return;
      const tracks = cloud.tracks.map(fromCloudTrack);
      const playlists = cloud.playlists as Playlist[];
      const commit = syncCommitQueue.then(async () => {
        if (requestId !== latestSyncRequest) return;
        await db.transaction("rw", db.tracks, db.playlists, async () => {
          await db.tracks.clear();
          await db.playlists.clear();
          if (tracks.length) await db.tracks.bulkPut(tracks);
          if (playlists.length) await db.playlists.bulkPut(playlists);
        });
        if (requestId !== latestSyncRequest) return;
        const cloudTrackIds = new Set(tracks.map((track) => track.id));
        const cloudPlaylistIds = new Set(playlists.map((playlist) => playlist.id));
        set((state) => ({
          tracks,
          playlists,
          authenticated: true,
          lastSyncedAt: cloud.syncedAt,
          cloudSyncing: false,
          selectedIds: state.selectedIds.filter((id) => cloudTrackIds.has(id)),
          currentTrackId: state.currentTrackId && cloudTrackIds.has(state.currentTrackId) ? state.currentTrackId : undefined,
          view: state.view.view === "playlist" && state.view.value && !cloudPlaylistIds.has(state.view.value) ? { view: "all" } : state.view,
          ...(silent ? {} : { notice: `Cloud library refreshed: ${tracks.length} track${tracks.length === 1 ? "" : "s"}, ${playlists.length} playlist${playlists.length === 1 ? "" : "s"}.` }),
        }));
      });
      syncCommitQueue = commit.catch(() => undefined);
      await commit;
      if (requestId !== latestSyncRequest) return;
      const handle = get().settings.syncFolderHandle;
      if (handle) void syncTracksToFolder(handle, tracks, playlists, get().settings.apiBaseUrl).then(({ written, removed }) => {
        if (written || removed) set({ notice: `Linked folder updated: ${written} downloaded, ${removed} removed.` });
      }).catch(() => undefined);
    } catch (error) {
      if (requestId === latestSyncRequest) set({
        cloudSyncing: false,
        ...(silent ? {} : { notice: "Cloud refresh failed. The last complete library snapshot is still shown." }),
      });
      throw error;
    }
  },

  async importFiles(files) {
    if (!get().authenticated) { set({ notice: "Unlock the private cloud library before uploading." }); return; }
    if (files.some((file) => file.name.toLowerCase().endsWith(".zip"))) {
      set({ importStatus: { current: 1, total: 1, fileName: files.find((file) => file.name.toLowerCase().endsWith(".zip"))?.name ?? "ZIP archive", action: "READING ZIP" } });
    }
    try {
      await get().syncLibrary(true);
    } catch {
      set({ notice: "Upload stopped because the current cloud library could not be loaded safely." });
      return;
    }
    const { settings } = get();
    const storage = createStorage("r2", settings.apiBaseUrl);
    const importedIds: string[] = [];
    let skipped = 0;
    let processed = 0;
    let discovered = 0;
    try {
      discovered = await visitMusicFiles(files, async (file) => {
      processed += 1;
      set({ importStatus: { current: processed, total: processed, fileName: file.name, action: "UPLOADING" } });
      let id: string | undefined;
      try {
        if (file.size > 100 * 1024 * 1024) throw new Error("Cloud uploads are limited to 100 MB per file.");
        const [metadata, fileHash] = await Promise.all([parseAudioMetadata(file), hashFile(file)]);
        if (get().tracks.some((track) => track.fileHash === fileHash)) { skipped += 1; return; }
        id = crypto.randomUUID();
        const storageKey = await storage.put(id, file);
        if (metadata.coverArt) await uploadArtwork(settings.apiBaseUrl, id, metadata.coverArt);
        const now = new Date().toISOString();
        const bpm = metadata.bpm;
        const musicalKey = metadata.musicalKey;
        const provenance: Track["provenance"] = {
          title: { source: "id3", confidence: 1 },
          ...(metadata.artist ? { artist: { source: "id3" as const, confidence: 1 } } : {}),
          ...(metadata.album ? { album: { source: "id3" as const, confidence: 1 } } : {}),
          ...(metadata.year ? { year: { source: "id3" as const, confidence: 1 } } : {}),
          ...(metadata.genres.length ? { genres: { source: "id3" as const, confidence: 1 } } : {}),
          ...(metadata.label ? { label: { source: "id3" as const, confidence: 1 } } : {}),
          ...(bpm ? { bpm: { source: metadata.bpmSource ?? "analysis", confidence: metadata.bpmConfidence ?? 0.55 } } : {}),
          ...(musicalKey ? { musicalKey: { source: metadata.keySource ?? "analysis", confidence: metadata.keyConfidence ?? 0.45 } } : {}),
        };
        const track: Track = {
          id, title: metadata.title, artist: metadata.artist, album: metadata.album,
          ...(metadata.year ? { year: metadata.year } : {}), genres: metadata.genres, label: metadata.label,
          ...(bpm ? { bpm } : {}), musicalKey, rating: 0, ratingSource: "plays", playCount: 0,
          favorite: false, tags: [], categories: [], duration: metadata.duration, fileName: file.name,
          fileSize: file.size, fileHash, storageMode: "r2", storageKey,
          ...(metadata.coverArt ? { coverArt: metadata.coverArt, coverArtUrl: `${settings.apiBaseUrl}/api/artwork/${id}` } : {}),
          addedAt: now, updatedAt: now, provenance,
        };
        await saveCloudTrack(settings.apiBaseUrl, track);
        await db.tracks.put(track);
        set((state) => ({ tracks: [track, ...state.tracks] }));
        importedIds.push(id);
      } catch (error) {
        if (id) await storage.remove(id).catch(() => undefined);
        set({ notice: `${file.name}: ${error instanceof Error ? error.message : "Upload failed."}` });
      }
      });
    } catch (error) {
      set({ importStatus: undefined, notice: error instanceof Error ? error.message : "The music archive could not be read." });
      return;
    }
    if (!discovered) { set({ importStatus: undefined, notice: "No MP3 files were found in that selection." }); return; }
    if (importedIds.length) await get().enrichTracks(importedIds);
    set({ importStatus: undefined, notice: `Uploaded ${importedIds.length} of ${discovered} MP3 file${discovered === 1 ? "" : "s"}${skipped ? `; skipped ${skipped} duplicate${skipped === 1 ? "" : "s"}` : ""}.` });
    if (importedIds.length) announceCloudLibraryChange();
    if (get().settings.syncFolderHandle) void get().syncFolder();
  },

  async updateTrack(id, patch) {
    const current = get().tracks.find((track) => track.id === id);
    if (!current) return;
    const updated: Track = { ...current, ...patch, id, storageMode: "r2", updatedAt: new Date().toISOString() };
    await saveCloudTrack(get().settings.apiBaseUrl, updated);
    await db.tracks.put(updated);
    set((state) => ({ tracks: state.tracks.map((track) => track.id === id ? updated : track) }));
    announceCloudLibraryChange();
  },

  async updateMany(ids, patch) {
    for (const id of ids) await get().updateTrack(id, patch);
  },

  async removeTracks(ids) {
    const uniqueIds = unique(ids).filter((id) => get().tracks.some((track) => track.id === id));
    if (!uniqueIds.length) return;
    const results = await Promise.allSettled(uniqueIds.map((id) => createStorage("r2", get().settings.apiBaseUrl).remove(id)));
    const removedIds = uniqueIds.filter((_, index) => results[index]?.status === "fulfilled");
    const removed = removedIds.length;
    if (removed) {
      const removedSet = new Set(removedIds);
      await db.tracks.bulkDelete(removedIds);
      set((state) => ({
        tracks: state.tracks.filter((track) => !removedSet.has(track.id)),
        selectedIds: state.selectedIds.filter((id) => !removedSet.has(id)),
        currentTrackId: state.currentTrackId && removedSet.has(state.currentTrackId) ? undefined : state.currentTrackId,
      }));
    }
    announceCloudLibraryChange();
    const failed = uniqueIds.length - removed;
    try {
      await get().syncLibrary(true);
    } catch {
      set({ notice: `Deleted ${removed} track${removed === 1 ? "" : "s"}. A fresh cloud check will run again automatically.` });
      return;
    }
    set({ notice: failed
      ? `Deleted ${removed} track${removed === 1 ? "" : "s"}; ${failed} could not be deleted. The cloud library was refreshed.`
      : `Deleted ${removed} track${removed === 1 ? "" : "s"} permanently from the cloud library.` });
  },

  async downloadTracks(ids) {
    const tracks = get().tracks.filter((track) => ids.includes(track.id));
    for (const [index, track] of tracks.entries()) {
      set({ importStatus: { current: index + 1, total: tracks.length, fileName: track.fileName, action: "SYNCING FOLDER" } });
      downloadBlob(await createStorage("r2", get().settings.apiBaseUrl).download(track.id), track.fileName);
    }
    set({ importStatus: undefined, notice: `Downloaded ${tracks.length} track${tracks.length === 1 ? "" : "s"}.` });
  },

  async linkSyncFolder() {
    try {
      const handle = await chooseSyncFolder();
      await get().updateSettings({ syncFolderHandle: handle });
      await get().syncFolder(true);
    } catch (error) { set({ notice: error instanceof Error ? error.message : "Folder could not be linked." }); }
  },

  async syncFolder(requestPermission = false) {
    const handle = get().settings.syncFolderHandle;
    if (!handle) { set({ notice: "Choose a sync folder in Preferences first." }); return; }
    set({ importStatus: { current: 1, total: 1, fileName: handle.name, action: "SYNCING FOLDER" } });
    try {
      const { written, removed } = await syncTracksToFolder(handle, get().tracks, get().playlists, get().settings.apiBaseUrl, requestPermission);
      const lastFolderSyncAt = new Date().toISOString();
      await get().updateSettings({ lastFolderSyncAt });
      set({ importStatus: undefined, notice: written || removed ? `${handle.name} updated: ${written} downloaded, ${removed} removed.` : `${handle.name} is up to date.` });
    } catch (error) { set({ importStatus: undefined, notice: error instanceof Error ? error.message : "Folder sync failed." }); }
  },

  async recordPlay(id) {
    try {
      const result = await (await apiRequest(get().settings.apiBaseUrl, `/api/tracks/${id}/play`, { method: "POST" })).json() as { playCount: number; rating: number; ratingSource: "plays" | "user" };
      set((state) => ({ tracks: state.tracks.map((track) => track.id === id ? { ...track, ...result } : track) }));
    } catch { /* Playback must continue if play counting is temporarily unavailable. */ }
  },

  setSelection: (selectedIds) => set({ selectedIds }),
  toggleSelection(id, additive = false, range) {
    if (range) { set({ selectedIds: unique([...get().selectedIds, ...range]) }); return; }
    if (!additive) { set({ selectedIds: [id] }); return; }
    const selected = new Set(get().selectedIds);
    if (selected.has(id)) selected.delete(id); else selected.add(id);
    set({ selectedIds: [...selected] });
  },
  setView: (view) => set({ view, selectedIds: [] }), setQuery: (query) => set({ query }),
  setGenreFilter: (genreFilter) => set({ genreFilter }), setLabelFilter: (labelFilter) => set({ labelFilter }),
  setSort(key) { set((state) => ({ sortKey: key, sortDirection: state.sortKey === key && state.sortDirection === "asc" ? "desc" : "asc" })); },
  setCurrentTrack: (currentTrackId) => set({ currentTrackId }),

  async createPlaylist(name, type, rules = []) {
    const now = new Date().toISOString();
    const playlist: Playlist = { id: crypto.randomUUID(), name: name.trim() || "Untitled Playlist", description: "", type, trackIds: [], rules, match: "all", createdAt: now, updatedAt: now };
    await saveCloudPlaylist(get().settings.apiBaseUrl, playlist);
    await db.playlists.add(playlist);
    set((state) => ({ playlists: [...state.playlists, playlist], view: { view: "playlist", value: playlist.id } }));
    announceCloudLibraryChange();
    if (get().settings.syncFolderHandle) void get().syncFolder();
  },
  async deletePlaylist(id) {
    const playlist = get().playlists.find((item) => item.id === id);
    if (!playlist) return;
    try {
      await apiRequest(get().settings.apiBaseUrl, `/api/playlists/${id}`, { method: "DELETE" });
      await db.playlists.delete(id);
      set((state) => ({
        playlists: state.playlists.filter((item) => item.id !== id),
        view: state.view.view === "playlist" && state.view.value === id ? { view: "all" } : state.view,
        selectedIds: state.view.view === "playlist" && state.view.value === id ? [] : state.selectedIds,
        notice: `Deleted playlist ${playlist.name}. Its tracks remain in the cloud library.`,
      }));
      announceCloudLibraryChange();
      if (get().settings.syncFolderHandle) void get().syncFolder();
    } catch (error) {
      set({ notice: error instanceof Error ? error.message : "The playlist could not be deleted." });
    }
  },
  async addToPlaylist(playlistId, trackIds) {
    const playlist = get().playlists.find((item) => item.id === playlistId);
    if (!playlist || playlist.type === "smart") return;
    const nextTrackIds = unique([...playlist.trackIds, ...trackIds]);
    const added = nextTrackIds.length - playlist.trackIds.length;
    const updated = { ...playlist, trackIds: nextTrackIds, updatedAt: new Date().toISOString() };
    await saveCloudPlaylist(get().settings.apiBaseUrl, updated);
    await db.playlists.put(updated);
    set((state) => ({ playlists: state.playlists.map((item) => item.id === playlistId ? updated : item), notice: added ? `Added ${added} track${added === 1 ? "" : "s"} to ${playlist.name}.` : `Those tracks are already in ${playlist.name}.` }));
    announceCloudLibraryChange();
    if (added && get().settings.syncFolderHandle) void get().syncFolder();
  },
  async removeFromPlaylist(playlistId, trackIds) {
    const playlist = get().playlists.find((item) => item.id === playlistId);
    if (!playlist || playlist.type === "smart") return;
    const removeIds = new Set(trackIds);
    const nextTrackIds = playlist.trackIds.filter((id) => !removeIds.has(id));
    const removed = playlist.trackIds.length - nextTrackIds.length;
    if (!removed) return;
    const updated = { ...playlist, trackIds: nextTrackIds, updatedAt: new Date().toISOString() };
    await saveCloudPlaylist(get().settings.apiBaseUrl, updated);
    await db.playlists.put(updated);
    set((state) => ({ playlists: state.playlists.map((item) => item.id === playlistId ? updated : item), selectedIds: [], notice: `Removed ${removed} track${removed === 1 ? "" : "s"} from ${playlist.name}.` }));
    announceCloudLibraryChange();
    if (get().settings.syncFolderHandle) void get().syncFolder();
  },

  async enrichTracks(ids) {
    const { settings } = get();
    let completed = 0;
    let unavailable = 0;
    for (const [index, id] of ids.entries()) {
      const track = get().tracks.find((item) => item.id === id);
      if (!track) continue;
      set({ importStatus: { current: index + 1, total: ids.length, fileName: track.fileName, action: "ANALYZING + TAGGING" } });
      try {
        const response = await apiRequest(settings.apiBaseUrl, "/api/enrich", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ trackId: id, title: track.title || undefined, artist: track.artist || undefined, album: track.album || undefined, year: track.year, bpm: track.bpm, duration: track.duration || undefined }),
        });
        const result = await response.json() as EnrichmentResponse;
        const withSuggestions = { ...track, suggestions: result.candidate };
        set((state) => ({ tracks: state.tracks.map((item) => item.id === id ? withSuggestions : item) }));
        const automatic: Array<keyof EnrichmentCandidate> = ["isrc", "musicbrainzRecordingId", "musicbrainzReleaseId"];
        if (!track.title) automatic.push("title");
        if (!track.artist) automatic.push("artist");
        if (!track.album) automatic.push("album");
        if (!track.year) automatic.push("year");
        if (!track.label) automatic.push("label");
        if (!track.genres.length) automatic.push("genres");
        automatic.push("tags", "categories");
        if (!track.coverArtUrl) automatic.push("coverArtUrl");
        await get().applySuggestions(id, automatic);
        completed += 1;
      } catch (error) { unavailable += 1; console.warn("Metadata service unavailable.", error); }
    }
    set({ importStatus: undefined, notice: completed ? `Auto-tagged ${completed} track${completed === 1 ? "" : "s"}. Existing values were preserved.` : unavailable ? "Metadata service is temporarily unavailable." : "Nothing to analyze." });
  },

  async applySuggestions(trackId, fields) {
    const track = get().tracks.find((item) => item.id === trackId);
    if (!track?.suggestions) return;
    const patch: Partial<Track> = {};
    const provenance = { ...track.provenance };
    const remainingSuggestions = { ...track.suggestions };
    for (const key of fields) {
      const suggestion = track.suggestions[key];
      if (!suggestion) continue;
      if (key === "artist" || key === "album" || key === "title" || key === "label") patch[key] = suggestion.value as string;
      else if (key === "year") patch.year = suggestion.value as number;
      else if (key === "genres") patch.genres = suggestion.value as string[];
      else if (key === "tags") patch.tags = unique([...track.tags, ...(suggestion.value as string[])]);
      else if (key === "categories") patch.categories = unique([...track.categories, ...(suggestion.value as string[])]);
      else if (key === "isrc") patch.isrc = suggestion.value as string;
      else if (key === "musicbrainzRecordingId") patch.musicbrainzRecordingId = suggestion.value as string;
      else if (key === "musicbrainzReleaseId") patch.musicbrainzReleaseId = suggestion.value as string;
      else if (key === "coverArtUrl") patch.coverArtUrl = suggestion.value as string;
      const trackKey = key === "coverArtUrl" ? "coverArt" : key;
      provenance[trackKey as keyof Track] = { source: suggestion.source, confidence: suggestion.confidence };
      delete remainingSuggestions[key];
    }
    await get().updateTrack(trackId, { ...patch, provenance, suggestions: Object.keys(remainingSuggestions).length ? remainingSuggestions : undefined });
  },

  async updateSettings(patch) {
    const settings = { ...get().settings, ...patch, id: "settings" as const, storageMode: "r2" as const, ...(import.meta.env.PROD ? { apiBaseUrl: configuredApiUrl } : {}) };
    await db.settings.put(settings);
    set({ settings });
  },
  clearNotice: () => set({ notice: undefined }),
}));
